import { AddToHomeScreenModal } from "@/components/client/AddToHomeScreenModal";
import { ImpersonationBanner } from "@/components/client/ImpersonationBanner";
import { PendingFileRow } from "@/components/PendingFileRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect, SelectItem as MultiSelectItem } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import {
  clientTicketService,
  ClientTicket,
  TicketAttachmentRef,
} from "@/services/clientTicket.service/clientTicket.api";
import {
  Loader2,
  MessageSquare,
  Mic,
  Paperclip,
  PlusCircle,
  Search,
  Sparkles,
  Square,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const statusLabel: Record<string, string> = {
  open: "Aberto",
  in_progress: "Em andamento",
  resolved: "Resolvido",
};

const priorityLabel: Record<string, string> = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const priorityBadgeVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  low: "secondary",
  medium: "outline",
  high: "default",
  urgent: "destructive",
};

type StatusFilter = "open" | "in_progress" | "resolved";

// "Em andamento" primeiro — é a aba que o cliente mais quer ver ao abrir a tela.
const STATUS_TABS: Array<{ key: StatusFilter; label: string }> = [
  { key: "in_progress", label: "Em andamento" },
  { key: "open", label: "Aberto" },
  { key: "resolved", label: "Resolvido" },
];

function formatDate(iso: string): string {
  return new Date(iso)
    .toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", " ");
}

const emptyForm = () => ({
  title: "",
  description: "",
  priority: "medium",
  pendingFiles: [] as File[],
});

export default function ClientTickets() {
  const { tenant: tenantParam } = useParams<{ tenant?: string }>();
  const [tickets, setTickets] = useState<ClientTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiPolishingDescription, setAiPolishingDescription] = useState(false);
  const [aiAudioBusy, setAiAudioBusy] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStreamRef = useRef<MediaStream | null>(null);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("in_progress");
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);

  const resetForm = useCallback(() => {
    setForm(emptyForm());
  }, []);

  useEffect(() => {
    const t = tenantParam?.trim();
    if (t) setClientTenant(t);
  }, [tenantParam]);

  const loadTickets = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    const [res] = await clientTicketService.getTickets({ page: 1, limit: 200 });
    if (res?.result) setTickets(res.result);
    if (!opts?.silent) setLoading(false);
  }, []);

  useEffect(() => {
    if (!clientAuthService.isAuthenticated()) {
      navigate(`${getClientPortalBase()}/login`);
      return;
    }
    void loadTickets();
  }, [navigate, loadTickets]);

  useEffect(() => {
    if (!clientAuthService.isAuthenticated()) return;
    const id = window.setInterval(() => void loadTickets({ silent: true }), 5000);
    return () => clearInterval(id);
  }, [loadTickets]);

  const openCreateDrawer = () => {
    resetForm();
    setCreateOpen(true);
  };

  const handleAiSuggestFromTitle = async () => {
    const titleDraft = form.title.trim();
    if (!titleDraft) {
      toast.error("Informe um título para a IA gerar o texto.");
      return;
    }
    setAiSuggesting(true);
    const [data, err] = await clientTicketService.suggestFromDraft(titleDraft);
    setAiSuggesting(false);
    if (err?.msg) {
      toast.error(err.msg);
      return;
    }
    if (data?.title && data?.description) {
      setForm((f) => ({ ...f, title: data.title, description: data.description }));
      toast.success("Título e descrição gerados.");
    }
  };

  const handlePolishDescription = async () => {
    const d = form.description.trim();
    if (!d) {
      toast.error("Escreva algo na descrição para melhorar.");
      return;
    }
    setAiPolishingDescription(true);
    const [data, err] = await clientTicketService.polishMessage(d);
    setAiPolishingDescription(false);
    if (err?.msg) {
      toast.error(err.msg);
      return;
    }
    if (data?.text) {
      setForm((f) => ({ ...f, description: data.text }));
      toast.success("Descrição melhorada.");
    }
  };

  const aiLocked = aiSuggesting || aiPolishingDescription || aiAudioBusy;

  /** Backend: transcreve + título/descrição; anexo vai na primeira mensagem ao salvar. */
  const processAudioFile = async (file: File) => {
    setAiAudioBusy(true);
    const [data, err] = await clientTicketService.draftFromAudio(file);
    setAiAudioBusy(false);
    if (err?.msg || !data?.title || !data?.description) {
      toast.error(err?.msg || "Falha ao processar o áudio");
      return;
    }
    setForm((f) => ({
      ...f,
      pendingFiles: [...f.pendingFiles, file],
      title: data.title,
      description: data.description,
    }));
    toast.success("Título e descrição gerados no servidor. Áudio será anexado ao criar o ticket.");
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      recordChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;
        const blob = new Blob(recordChunksRef.current, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], `gravacao-${Date.now()}.webm`, {
          type: blob.type || "audio/webm",
        });
        await processAudioFile(file);
      };
      mr.start(200);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const handleAudioFileTranscribe = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await processAudioFile(file);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = form.title.trim();
    const d = form.description.trim();
    if (!t || !d) {
      toast.error("Preencha título e descrição.");
      return;
    }
    setSubmitting(true);
    const attachments: TicketAttachmentRef[] = [];
    for (const file of form.pendingFiles) {
      const [ref, err] = await clientTicketService.uploadAttachment(file);
      if (err?.msg || !ref) {
        toast.error(err?.msg || "Falha no envio de arquivo");
        setSubmitting(false);
        return;
      }
      attachments.push(ref);
    }
    const [ticket, error] = await clientTicketService.createTicket({
      title: t,
      description: d,
      priority: form.priority,
      attachments: attachments.length ? attachments : undefined,
    });
    setSubmitting(false);
    if (error?.msg) {
      toast.error(error.msg);
      return;
    }
    if (ticket?._id) {
      toast.success("Ticket criado.");
      setCreateOpen(false);
      resetForm();
      await loadTickets();
      navigate(`${getClientPortalBase()}/tickets/${encodeURIComponent(ticket.protocolId)}`);
    }
  };

  const handleLogout = () => {
    clientAuthService.logout();
  };

  const user = clientAuthService.getCurrentUser();
  const isImpersonating = clientAuthService.isImpersonating();

  const statusCounts = useMemo(
    () => ({
      open: tickets.filter((t) => t.status === "open").length,
      in_progress: tickets.filter((t) => t.status === "in_progress").length,
      resolved: tickets.filter((t) => t.status === "resolved").length,
    }),
    [tickets]
  );

  const filteredTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (t.status !== statusFilter) return false;
      if (priorityFilter.length && !priorityFilter.includes(t.priority)) return false;
      if (term) {
        const haystack = `${t.title} ${t.protocolId} ${t.description || ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter, priorityFilter]);

  const hasActiveFilters = priorityFilter.length > 0 || search.trim().length > 0;

  const clearFilters = () => {
    setSearch("");
    setPriorityFilter([]);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <AddToHomeScreenModal user={user} />
      {isImpersonating && <ImpersonationBanner />}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-primary">Baita</span>
            <span className="text-xl font-bold">Ticket</span>
            <span className="text-muted-foreground">· Portal do Cliente</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden sm:inline text-sm text-muted-foreground">{user.name}</span>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Meus Tickets</h1>
          <Button type="button" onClick={openCreateDrawer} className="gap-2">
            <PlusCircle className="h-4 w-4" />
            Novo ticket
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Você ainda não tem tickets.</p>
              <Button type="button" onClick={openCreateDrawer} className="gap-2">
                <PlusCircle className="h-4 w-4" />
                Abrir primeiro ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="grid gap-6 pt-6">
              <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <TabsList>
                  {STATUS_TABS.map((tab) => (
                    <TabsTrigger key={tab.key} value={tab.key} className="gap-2">
                      {tab.label}
                      <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                        {statusCounts[tab.key]}
                      </Badge>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por título, descrição ou protocolo..."
                    className="pl-8"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <MultiSelect
                  value={priorityFilter}
                  onValueChange={setPriorityFilter}
                  placeholder="Prioridade"
                  className="sm:w-48"
                >
                  {Object.entries(priorityLabel).map(([value, label]) => (
                    <MultiSelectItem key={value} value={value}>
                      {label}
                    </MultiSelectItem>
                  ))}
                </MultiSelect>
                {hasActiveFilters && (
                  <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={clearFilters}>
                    <X className="h-3.5 w-3.5" />
                    Limpar filtros
                  </Button>
                )}
              </div>

              {filteredTickets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nenhum ticket encontrado</div>
              ) : (
                <>
                  {/* Tabela para desktop */}
                  <div className="hidden md:block rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Protocolo</TableHead>
                          <TableHead>Título</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Prioridade</TableHead>
                          <TableHead>Data de criação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTickets.map((t) => (
                          <TableRow
                            key={t._id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() =>
                              navigate(`${getClientPortalBase()}/tickets/${encodeURIComponent(t.protocolId)}`)
                            }
                          >
                            <TableCell className="font-medium">{t.protocolId}</TableCell>
                            <TableCell>{t.title}</TableCell>
                            <TableCell>{statusLabel[t.status] || t.status}</TableCell>
                            <TableCell>
                              <Badge variant={priorityBadgeVariant[t.priority] || "outline"} className="text-xs">
                                {priorityLabel[t.priority] || t.priority}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(t.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Cards para mobile */}
                  <div className="md:hidden space-y-4">
                    {filteredTickets.map((t) => (
                      <div
                        key={t._id}
                        className="p-4 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`${getClientPortalBase()}/tickets/${encodeURIComponent(t.protocolId)}`)}
                      >
                        <div className="flex justify-between items-start mb-2 gap-2">
                          <div className="font-medium">{t.protocolId}</div>
                          <Badge variant={priorityBadgeVariant[t.priority] || "outline"} className="text-xs shrink-0">
                            {priorityLabel[t.priority] || t.priority}
                          </Badge>
                        </div>
                        <h3 className="font-semibold mb-2">{t.title || t.protocolId}</h3>
                        {t.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{t.description}</p>
                        )}
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex justify-between">
                            <span>Status:</span>
                            <span>{statusLabel[t.status] || t.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Criado em:</span>
                            <span>{formatDate(t.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      <Sheet
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Novo ticket</SheetTitle>
            <SheetDescription>
              Preencha os dados abaixo. O ticket só será criado ao clicar em Salvar.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmitCreate} className="flex flex-col gap-4 flex-1 mt-4">
            <div className="space-y-2">
              <Label htmlFor="drawer-title">Título</Label>
              <Input
                id="drawer-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Resumo do pedido"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="drawer-description">Descrição</Label>
              <Textarea
                id="drawer-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Explique o problema ou o pedido com o máximo de detalhes."
                rows={6}
                required
              />
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                IA e áudio
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-9 justify-start gap-2"
                  disabled={aiLocked}
                  onClick={handleAiSuggestFromTitle}
                >
                  {aiSuggesting ? (
                    <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  ) : (
                    <Sparkles className="h-3 w-3 shrink-0" />
                  )}
                  <span className="text-left leading-tight">
                    Gerar título e descrição
                    <span className="block text-[10px] font-normal opacity-80">a partir do campo Título</span>
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 justify-start gap-2"
                  disabled={aiLocked}
                  onClick={handlePolishDescription}
                >
                  {aiPolishingDescription ? (
                    <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  ) : (
                    <Sparkles className="h-3 w-3 shrink-0" />
                  )}
                  <span className="text-left leading-tight">
                    Melhorar só a descrição
                    <span className="block text-[10px] font-normal opacity-80">texto já escrito acima</span>
                  </span>
                </Button>
              </div>
              <div className="border-t border-border/60 pt-3 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Áudio: transcreve, anexa o arquivo ao chamado e preenche título e descrição com a IA.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {!isRecording ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      disabled={aiLocked}
                      onClick={startRecording}
                    >
                      <Mic className="h-3 w-3" />
                      Gravar
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="gap-1"
                      disabled={aiAudioBusy}
                      onClick={stopRecording}
                    >
                      <Square className="h-3 w-3" />
                      Parar
                    </Button>
                  )}
                  <input
                    ref={audioInputRef}
                    type="file"
                    className="sr-only"
                    accept="audio/*,.webm,.mp3,.wav,.m4a,.ogg"
                    onChange={handleAudioFileTranscribe}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={aiLocked || isRecording}
                    onClick={() => audioInputRef.current?.click()}
                  >
                    {aiAudioBusy && !isRecording ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Arquivo de áudio
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div>
                <Label>Anexos ao chamado</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Vão na primeira mensagem ao salvar (inclui o áudio da seção IA, se houver).
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  tabIndex={-1}
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setForm((f) => ({
                      ...f,
                      pendingFiles: [...f.pendingFiles, ...files],
                    }));
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-4 w-4" />
                  Adicionar arquivos
                </Button>
              </div>
              {form.pendingFiles.length > 0 && (
                <ul className="text-sm border rounded-md p-2 max-h-56 overflow-y-auto">
                  {form.pendingFiles.map((f, i) => (
                    <PendingFileRow
                      key={`${f.name}-${i}`}
                      file={f}
                      onRemove={() =>
                        setForm((prev) => ({
                          ...prev,
                          pendingFiles: prev.pendingFiles.filter((_, j) => j !== i),
                        }))
                      }
                    />
                  ))}
                </ul>
              )}
            </div>
            <SheetFooter className="gap-2 sm:gap-0 mt-auto pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando…
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
